import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  api,
  ApiError,
  type FeePreview,
  type OfficePackageCode,
  type Payer,
  type Plan,
  type PriceList,
  type PriceListDraft,
  type ReferencePrice,
} from '../api';
import { date, money, type Dictionary, type Locale } from '../i18n';
import * as Icon from '@firmeto/design/Icons';

/**
 * Cenniki (E11-S7).
 *
 * Cennik obowiązuje od pierwszego dnia miesiąca i — gdy już obowiązuje — jest niezmienny, bo
 * naliczenia liczy się po cenniku z ostatniego dnia miesiąca. Zmiana to nowa wersja od
 * przyszłego miesiąca; wersję, która jeszcze nie weszła, można poprawić albo usunąć.
 *
 * Reguła „nigdy drożej niż iFirma” jest tu blokadą: formularz pokazuje zapas wobec najnowszej
 * obserwacji ceny iFirma, a API odrzuca cenę powyżej niej. Podgląd opłaty liczy ten sam
 * kalkulator, którym nalicza się miesiące.
 */

const PLANS: Plan[] = ['RYCZALT', 'KPIR', 'FULL'];
const PACKAGES: OfficePackageCode[] = ['BIURO_10', 'BIURO_50', 'BIURO_200'];

function failureText(failure: unknown): string {
  return failure instanceof ApiError ? failure.message : String(failure);
}

/** Kwota z API jako tekst do pola formularza — z dwoma miejscami, przecinkiem jak w Polsce. */
function amount(value: number): string {
  return value.toFixed(2).replace('.', ',');
}

/** To, co operator wpisał, w zapisie, który API parsuje dokładnie. */
function decimal(value: string): string {
  return value.replace(/\s/g, '').replace(',', '.').trim();
}

function firstOfMonthAfterNext(): string {
  const now = new Date();
  return new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() + 2, 1)).toISOString().slice(0, 10);
}

function draftOf(list: PriceList): PriceListDraft {
  return {
    validFrom: firstOfMonthAfterNext(),
    vatRate: list.vatRate.toString().replace('.', ','),
    minimumFeeGross: amount(list.minimumFeeGross),
    revenueShareCap: list.revenueShareCap.toString().replace('.', ','),
    trialMonths: list.trialMonths,
    note: '',
    plans: PLANS.map((plan) => {
      const row = list.plans.find((p) => p.plan === plan);
      return {
        plan,
        companyNet: row ? amount(row.companyNet) : '',
        officeNet: row ? amount(row.officeNet) : '',
        storeGross: row ? amount(row.storeGross) : '',
      };
    }),
    packages: PACKAGES.map((code) => ({
      packageCode: code,
      ceilingGross: amount(list.packages.find((p) => p.packageCode === code)?.ceilingGross ?? 0),
    })),
  };
}

/** Poprawka istniejącej przyszłej wersji — jej data i uzasadnienie zostają. */
function editDraftOf(list: PriceList): PriceListDraft {
  return { ...draftOf(list), validFrom: list.validFrom, note: list.note ?? '' };
}

export function Pricing({ t, locale }: { t: Dictionary; locale: Locale }) {
  const [lists, setLists] = useState<PriceList[] | null>(null);
  const [references, setReferences] = useState<ReferencePrice[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState<string | null>(null);
  const [editing, setEditing] = useState<{ id: string | null; draft: PriceListDraft } | null>(null);

  const load = useCallback(async () => {
    try {
      const [loadedLists, loadedReferences] = await Promise.all([api.priceLists(), api.priceReferences()]);
      setLists(loadedLists);
      setReferences(loadedReferences);
    } catch (failure) {
      setError(failureText(failure));
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const latestReference = useMemo(() => {
    const byPlan = new Map<Plan, ReferencePrice>();
    for (const reference of references) {
      if (reference.competitor !== 'ifirma') continue;
      const known = byPlan.get(reference.plan);
      if (!known || reference.observedOn > known.observedOn || (reference.observedOn === known.observedOn && reference.createdAt > known.createdAt)) {
        byPlan.set(reference.plan, reference);
      }
    }
    return byPlan;
  }, [references]);

  async function remove(list: PriceList) {
    if (!window.confirm(t.pricing.removeConfirm)) return;
    setError(null);
    try {
      await api.deletePriceList(list.id);
      setSaved(t.pricing.saved);
      await load();
    } catch (failure) {
      setError(failureText(failure));
    }
  }

  const current = lists?.find((list) => list.status === 'CURRENT') ?? null;

  return (
    <>
      <div className="hd">
        <div>
          <h3>{t.pricing.title}</h3>
          <p>{t.pricing.lead}</p>
        </div>
        <div className="sp">
          {current && !editing && (
            <button type="button" className="btn" onClick={() => setEditing({ id: null, draft: draftOf(current) })}>
              <Icon.Plus />
              {t.pricing.newVersion}
            </button>
          )}
        </div>
      </div>

      {error && (
        <div className="alert a-bad">
          <Icon.Error />
          <div>
            <b>{error}</b>
          </div>
        </div>
      )}
      {saved && !error && (
        <div className="alert a-ok">
          <Icon.Check />
          <div>
            <b>{saved}</b>
          </div>
        </div>
      )}

      {lists === null && <p className="muted">{t.loading}</p>}

      {editing && (
        <PriceListForm
          t={t}
          locale={locale}
          id={editing.id}
          draft={editing.draft}
          reference={latestReference}
          onChange={(draft) => setEditing({ ...editing, draft })}
          onCancel={() => setEditing(null)}
          onSaved={async () => {
            setEditing(null);
            setError(null);
            setSaved(t.pricing.saved);
            await load();
          }}
        />
      )}

      {current && !editing && <PriceListView t={t} locale={locale} list={current} title={t.pricing.current} />}

      {lists && lists.length > 0 && (
        <div className="panel">
          <div className="hd">
            <div>
              <h4>{t.pricing.versions}</h4>
            </div>
          </div>
          <div className="tbl">
            <table>
              <thead>
                <tr>
                  <th>{t.pricing.validFrom}</th>
                  <th>{t.offices.status}</th>
                  <th className="wrap">{t.pricing.note}</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {lists.map((list) => (
                  <tr key={list.id}>
                    <td className="mono">{date(list.validFrom, locale)}</td>
                    <td>
                      <span className={`chip ${list.status === 'CURRENT' ? 'st-ok' : list.status === 'FUTURE' ? 'st-info' : 'st-draft'}`}>
                        {t.pricing.status[list.status]}
                      </span>
                    </td>
                    <td className="wrap">{list.note ?? ''}</td>
                    <td className="n">
                      <div className="sp">
                        <button type="button" className="btn ghost sm" onClick={() => setEditing({ id: null, draft: draftOf(list) })}>
                          {t.pricing.newVersion}
                        </button>
                        {list.editable && (
                          <>
                            <button type="button" className="btn ghost sm" onClick={() => setEditing({ id: list.id, draft: editDraftOf(list) })}>
                              {t.pricing.edit}
                            </button>
                            <button type="button" className="btn ghost sm danger" onClick={() => void remove(list)}>
                              {t.pricing.remove}
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <References t={t} locale={locale} references={references} onAdded={load} />
    </>
  );
}

function PriceListView({ t, locale, list, title }: { t: Dictionary; locale: Locale; list: PriceList; title: string }) {
  return (
    <div className="panel">
      <div className="hd">
        <div>
          <h4>{title}</h4>
          <p>
            {t.pricing.validFrom} {date(list.validFrom, locale)}
            {list.note ? ` · ${list.note}` : ''}
          </p>
        </div>
      </div>
      <div className="tbl">
        <table>
          <thead>
            <tr>
              <th className="wrap">{t.pricing.plan}</th>
              <th className="n">{t.pricing.companyNet}</th>
              <th className="n">{t.pricing.companyGross}</th>
              <th className="n">{t.pricing.officeNet}</th>
              <th className="n">{t.pricing.storeGross}</th>
              <th className="n">{t.pricing.reference}</th>
            </tr>
          </thead>
          <tbody>
            {list.plans.map((row) => (
              <tr key={row.plan}>
                <td className="wrap">
                  <b>{t.dict.plans[row.plan]}</b>
                </td>
                <td className="n mono">{money(row.companyNet, locale)}</td>
                <td className="n mono">{money(row.companyGross, locale)}</td>
                <td className="n mono">{money(row.officeNet, locale)}</td>
                <td className="n mono">{money(row.storeGross, locale)}</td>
                <td className="n mono">{row.referenceNet === null ? t.pricing.noReference : money(row.referenceNet, locale)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="hint">
        {t.pricing.vatRate}: {(list.vatRate * 100).toFixed(0)}% · {t.pricing.minimumFee}: {money(list.minimumFeeGross, locale)} ·{' '}
        {t.pricing.revenueCap}: {(list.revenueShareCap * 100).toFixed(0)}% · {t.pricing.trialMonths}: {list.trialMonths} ·{' '}
        {t.pricing.packages}:{' '}
        {list.packages.map((p) => `${t.offices.packages[p.packageCode.toLowerCase()] ?? p.packageCode} ${money(p.ceilingGross, locale)}`).join(', ')}
      </p>
    </div>
  );
}

function PriceListForm({
  t,
  locale,
  id,
  draft,
  reference,
  onChange,
  onCancel,
  onSaved,
}: {
  t: Dictionary;
  locale: Locale;
  /** Identyfikator poprawianej wersji; brak — nowa wersja. */
  id: string | null;
  draft: PriceListDraft;
  reference: Map<Plan, ReferencePrice>;
  onChange: (draft: PriceListDraft) => void;
  onCancel: () => void;
  onSaved: () => Promise<void>;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function setPlan(plan: Plan, field: 'companyNet' | 'officeNet' | 'storeGross', value: string) {
    onChange({ ...draft, plans: draft.plans.map((row) => (row.plan === plan ? { ...row, [field]: value } : row)) });
  }

  function setPackage(code: OfficePackageCode, value: string) {
    onChange({ ...draft, packages: draft.packages.map((row) => (row.packageCode === code ? { ...row, ceilingGross: value } : row)) });
  }

  const vat = Number(decimal(draft.vatRate)) || 0;
  const gross = (net: string) => {
    const value = Number(decimal(net));
    return Number.isFinite(value) ? Math.round(value * (1 + vat) * 100) / 100 : null;
  };
  const above = (plan: Plan, net: string) => {
    const ref = reference.get(plan);
    const value = Number(decimal(net));
    return ref !== undefined && Number.isFinite(value) && value > ref.netMonthly;
  };
  const blocked = draft.plans.some((row) => above(row.plan, row.companyNet) || above(row.plan, row.officeNet));

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    const payload = toPayload(draft);
    try {
      if (id) await api.updatePriceList(id, payload);
      else await api.createPriceList(payload);
      await onSaved();
    } catch (failure) {
      setError(failureText(failure));
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <form className="panel" onSubmit={(event) => void submit(event)}>
        <div className="hd">
          <div>
            <h4>{id ? t.pricing.edit : t.pricing.newVersion}</h4>
            <p>{t.pricing.newVersionLead}</p>
          </div>
        </div>
        {error && (
          <div className="alert a-bad">
            <Icon.Error />
            <div>
              <b>{error}</b>
            </div>
          </div>
        )}
        <div className="row">
          <label className="fld">
            <span>{t.pricing.validFrom}</span>
            <input className="inp mono" type="date" value={draft.validFrom} onChange={(event) => onChange({ ...draft, validFrom: event.target.value })} required />
          </label>
          <label className="fld" style={{ flex: 2, minWidth: 320 }}>
            <span>{t.pricing.note}</span>
            <input className="inp" value={draft.note} placeholder={t.pricing.noteHint} onChange={(event) => onChange({ ...draft, note: event.target.value })} required />
          </label>
        </div>

        <div className="tbl">
          <table>
            <thead>
              <tr>
                <th className="wrap">{t.pricing.plan}</th>
                <th>{t.pricing.companyNet}</th>
                <th className="n">{t.pricing.companyGross}</th>
                <th>{t.pricing.officeNet}</th>
                <th>{t.pricing.storeGross}</th>
                <th className="wrap">{t.pricing.reference}</th>
              </tr>
            </thead>
            <tbody>
              {draft.plans.map((row) => {
                const ref = reference.get(row.plan);
                const companyGross = gross(row.companyNet);
                const over = above(row.plan, row.companyNet) || above(row.plan, row.officeNet);
                return (
                  <tr key={row.plan}>
                    <td className="wrap">
                      <b>{t.dict.plans[row.plan]}</b>
                    </td>
                    <td>
                      <input
                        className="inp mono"
                        style={{ maxWidth: 120 }}
                        inputMode="decimal"
                        aria-label={`${t.pricing.companyNet} ${t.dict.plans[row.plan]}`}
                        value={row.companyNet}
                        onChange={(event) => setPlan(row.plan, 'companyNet', event.target.value)}
                        required
                      />
                    </td>
                    <td className="n mono">{companyGross === null ? '' : money(companyGross, locale)}</td>
                    <td>
                      <input
                        className="inp mono"
                        style={{ maxWidth: 120 }}
                        inputMode="decimal"
                        aria-label={`${t.pricing.officeNet} ${t.dict.plans[row.plan]}`}
                        value={row.officeNet}
                        onChange={(event) => setPlan(row.plan, 'officeNet', event.target.value)}
                        required
                      />
                    </td>
                    <td>
                      <input
                        className="inp mono"
                        style={{ maxWidth: 120 }}
                        inputMode="decimal"
                        aria-label={`${t.pricing.storeGross} ${t.dict.plans[row.plan]}`}
                        value={row.storeGross}
                        onChange={(event) => setPlan(row.plan, 'storeGross', event.target.value)}
                        required
                      />
                    </td>
                    <td className="wrap">
                      {ref ? (
                        <>
                          <span className="mono">{money(ref.netMonthly, locale)}</span>{' '}
                          {over ? (
                            <span className="chip st-bad">{t.pricing.aboveReference}</span>
                          ) : (
                            <span className="hint">
                              {t.pricing.headroom} {money(ref.netMonthly - Math.max(Number(decimal(row.companyNet)) || 0, Number(decimal(row.officeNet)) || 0), locale)}
                            </span>
                          )}
                        </>
                      ) : (
                        <span className="chip st-warn">{t.pricing.noReference}</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div className="row">
          <label className="fld">
            <span>{t.pricing.vatRate}</span>
            <input className="inp mono" style={{ maxWidth: 100 }} inputMode="decimal" value={draft.vatRate} onChange={(event) => onChange({ ...draft, vatRate: event.target.value })} required />
          </label>
          <label className="fld">
            <span>{t.pricing.minimumFee}</span>
            <input className="inp mono" style={{ maxWidth: 120 }} inputMode="decimal" value={draft.minimumFeeGross} onChange={(event) => onChange({ ...draft, minimumFeeGross: event.target.value })} required />
          </label>
          <label className="fld">
            <span>{t.pricing.revenueCap}</span>
            <input className="inp mono" style={{ maxWidth: 100 }} inputMode="decimal" value={draft.revenueShareCap} onChange={(event) => onChange({ ...draft, revenueShareCap: event.target.value })} required />
          </label>
          <label className="fld">
            <span>{t.pricing.trialMonths}</span>
            <input
              className="inp mono"
              style={{ maxWidth: 80 }}
              type="number"
              min={0}
              max={12}
              value={draft.trialMonths}
              onChange={(event) => onChange({ ...draft, trialMonths: Number(event.target.value) })}
              required
            />
          </label>
        </div>

        <div className="hd">
          <div>
            <h4>{t.pricing.packages}</h4>
          </div>
        </div>
        <div className="row">
          {draft.packages.map((row) => (
            <label className="fld" key={row.packageCode}>
              <span>{t.offices.packages[row.packageCode.toLowerCase()] ?? row.packageCode}</span>
              <input className="inp mono" style={{ maxWidth: 140 }} inputMode="decimal" value={row.ceilingGross} onChange={(event) => setPackage(row.packageCode, event.target.value)} required />
            </label>
          ))}
        </div>

        <div className="row">
          <button type="submit" className="btn" disabled={busy || blocked || draft.note.trim() === ''}>
            {id ? t.pricing.saveChanges : t.pricing.save}
          </button>
          <button type="button" className="btn ghost" onClick={onCancel} disabled={busy}>
            {t.pricing.cancel}
          </button>
        </div>
      </form>

      <FeePreviewPanel t={t} locale={locale} draft={draft} />
    </>
  );
}

function toPayload(draft: PriceListDraft): PriceListDraft {
  return {
    ...draft,
    vatRate: decimal(draft.vatRate),
    minimumFeeGross: decimal(draft.minimumFeeGross),
    revenueShareCap: decimal(draft.revenueShareCap),
    note: draft.note.trim(),
    plans: draft.plans.map((row) => ({ ...row, companyNet: decimal(row.companyNet), officeNet: decimal(row.officeNet), storeGross: decimal(row.storeGross) })),
    packages: draft.packages.map((row) => ({ ...row, ceilingGross: decimal(row.ceilingGross) })),
  };
}

/** Opłata z projektu cennika — liczona po stronie API tym samym kalkulatorem, co naliczenie. */
function FeePreviewPanel({ t, locale, draft }: { t: Dictionary; locale: Locale; draft: PriceListDraft }) {
  const [sample, setSample] = useState<{ plan: Plan; payer: Payer; revenue: string }>({ plan: 'KPIR', payer: 'COMPANY', revenue: '2 000,00' });
  const [preview, setPreview] = useState<FeePreview | null>(null);
  const [error, setError] = useState<string | null>(null);

  const request = useMemo(() => JSON.stringify({ draft, sample }), [draft, sample]);

  useEffect(() => {
    const payload = toPayload(draft);
    const complete = payload.plans.every((row) => row.companyNet !== '' && row.officeNet !== '' && row.storeGross !== '') && payload.validFrom !== '';
    if (!complete) {
      setPreview(null);
      return;
    }
    const timer = setTimeout(() => {
      api
        .previewFee(payload, sample.plan, sample.payer, decimal(sample.revenue) || '0')
        .then((result) => {
          setPreview(result);
          setError(null);
        })
        .catch((failure) => {
          setPreview(null);
          setError(failureText(failure));
        });
    }, 300);
    return () => clearTimeout(timer);
    // `request` jest kluczem treści — porównanie po wartości
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [request]);

  return (
    <div className="panel">
      <div className="hd">
        <div>
          <h4>{t.pricing.preview}</h4>
          <p>{t.pricing.previewLead}</p>
        </div>
      </div>
      <div className="row">
        <label className="fld">
          <span>{t.pricing.plan}</span>
          <select className="inp" value={sample.plan} onChange={(event) => setSample({ ...sample, plan: event.target.value as Plan })}>
            {PLANS.map((plan) => (
              <option key={plan} value={plan}>
                {t.dict.plans[plan]}
              </option>
            ))}
          </select>
        </label>
        <label className="fld">
          <span>{t.pricing.payer}</span>
          <select className="inp" value={sample.payer} onChange={(event) => setSample({ ...sample, payer: event.target.value as Payer })}>
            <option value="COMPANY">{t.offices.payers.COMPANY}</option>
            <option value="OFFICE">{t.offices.payers.OFFICE}</option>
          </select>
        </label>
        <label className="fld">
          <span>{t.pricing.revenue}</span>
          <input className="inp mono" style={{ maxWidth: 160 }} inputMode="decimal" value={sample.revenue} onChange={(event) => setSample({ ...sample, revenue: event.target.value })} />
        </label>
      </div>
      {error && (
        <div className="alert a-bad">
          <Icon.Error />
          <div>
            <b>{error}</b>
          </div>
        </div>
      )}
      {preview && (
        <div className="tbl">
          <table>
            <thead>
              <tr>
                <th className="n">{t.pricing.planPrice}</th>
                <th className="n">{t.pricing.cap}</th>
                <th className="n">{t.pricing.fee}</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td className="n mono">{money(preview.planPriceGross, locale)}</td>
                <td className="n mono">{money(preview.revenueCapGross, locale)}</td>
                <td className="n mono">
                  <b>{money(preview.feeGross, locale)}</b>
                  {preview.zeroReason && <span className="chip st-info">{t.pricing.zero[preview.zeroReason] ?? preview.zeroReason}</span>}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function References({ t, locale, references, onAdded }: { t: Dictionary; locale: Locale; references: ReferencePrice[]; onAdded: () => Promise<void> }) {
  const [form, setForm] = useState({ plan: 'KPIR' as Plan, netMonthly: '', observedOn: new Date().toISOString().slice(0, 10), source: '', note: '' });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    setSaved(false);
    try {
      await api.addPriceReference({ plan: form.plan, netMonthly: decimal(form.netMonthly), observedOn: form.observedOn, source: form.source.trim(), note: form.note.trim() || null });
      setForm({ ...form, netMonthly: '', source: '', note: '' });
      setSaved(true);
      await onAdded();
    } catch (failure) {
      setError(failureText(failure));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="panel">
      <div className="hd">
        <div>
          <h4>{t.pricing.references}</h4>
          <p>{t.pricing.referencesLead}</p>
        </div>
      </div>
      {error && (
        <div className="alert a-bad">
          <Icon.Error />
          <div>
            <b>{error}</b>
          </div>
        </div>
      )}
      {saved && !error && (
        <div className="alert a-ok">
          <Icon.Check />
          <div>{t.pricing.referenceSaved}</div>
        </div>
      )}
      <form className="row" onSubmit={(event) => void submit(event)}>
        <label className="fld">
          <span>{t.pricing.plan}</span>
          <select className="inp" value={form.plan} onChange={(event) => setForm({ ...form, plan: event.target.value as Plan })}>
            {PLANS.map((plan) => (
              <option key={plan} value={plan}>
                {t.dict.plans[plan]}
              </option>
            ))}
          </select>
        </label>
        <label className="fld">
          <span>{t.pricing.netMonthly}</span>
          <input className="inp mono" style={{ maxWidth: 120 }} inputMode="decimal" value={form.netMonthly} onChange={(event) => setForm({ ...form, netMonthly: event.target.value })} required />
        </label>
        <label className="fld">
          <span>{t.pricing.observedOn}</span>
          <input className="inp mono" type="date" value={form.observedOn} onChange={(event) => setForm({ ...form, observedOn: event.target.value })} required />
        </label>
        <label className="fld" style={{ flex: 2, minWidth: 260 }}>
          <span>{t.pricing.source}</span>
          <input className="inp" value={form.source} placeholder="https://www.ifirma.pl/cennik" onChange={(event) => setForm({ ...form, source: event.target.value })} required />
        </label>
        <label className="fld" style={{ flex: 2, minWidth: 260 }}>
          <span>{t.pricing.note}</span>
          <input className="inp" value={form.note} onChange={(event) => setForm({ ...form, note: event.target.value })} />
        </label>
        <div className="fld">
          <span>&nbsp;</span>
          <button type="submit" className="btn" disabled={busy || form.netMonthly.trim() === '' || form.source.trim() === ''}>
            {t.pricing.addReference}
          </button>
        </div>
      </form>
      {references.length > 0 && (
        <div className="tbl">
          <table>
            <thead>
              <tr>
                <th>{t.pricing.observedOn}</th>
                <th>{t.pricing.plan}</th>
                <th className="n">{t.pricing.netMonthly}</th>
                <th className="wrap">{t.pricing.source}</th>
                <th className="wrap">{t.pricing.note}</th>
              </tr>
            </thead>
            <tbody>
              {references.map((reference) => (
                <tr key={reference.id}>
                  <td className="mono">{date(reference.observedOn, locale)}</td>
                  <td>{t.dict.plans[reference.plan]}</td>
                  <td className="n mono">{money(reference.netMonthly, locale)}</td>
                  <td className="wrap">{reference.source}</td>
                  <td className="wrap">{reference.note ?? ''}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
